import React, { useEffect, useState } from "react";
import { api } from "../../lib/apiClient";
import { Plus, Trash2, GripVertical, Save, ChevronDown, ChevronUp, Edit3, GitBranch } from "lucide-react";
import { toast } from "../../components/ui/sonner";

// Possible answer values used to anchor a branching rule, per question type.
// Returns null when the type doesn't support branching (text, signature, photo, …).
function branchingValuesFor(q) {
  if (!q) return null;
  if (q.type === "yes_no") return ["Yes", "No"];
  if (q.type === "dropdown" || q.type === "multi_select") return (q.options || []).filter(Boolean);
  return null;
}

const TYPES = [
  { v: "short_text", l: "Short text" },
  { v: "long_text", l: "Long text" },
  { v: "number", l: "Number" },
  { v: "dropdown", l: "Dropdown" },
  { v: "multi_select", l: "Multi-select" },
  { v: "yes_no", l: "Yes / No" },
  { v: "photo", l: "Photo (camera/library)" },
  { v: "barcode", l: "Barcode / QR code (scan or library)" },
  { v: "date", l: "Date" },
  { v: "signature", l: "Signature pad" },
  { v: "measurement", l: "Measurement (W × H × D)" },
];

// Which quote field each survey question can pre-fill on convert-to-quote.
// Labels are user-friendly; values match QUOTE_FIELD_MAPPINGS in the backend.
const QUOTE_FIELD_OPTIONS = [
  { v: "", l: "— don't pre-fill anything —" },
  { v: "customer_name", l: "Customer name" },
  { v: "site_name", l: "Site name" },
  { v: "postcode", l: "Postcode" },
  { v: "site_address", l: "Site address" },
  { v: "job_type", l: "Job type (Install / Repair)" },
  { v: "category", l: "Product category" },
  { v: "branch", l: "Rate / branch (e.g. Plymouth Basic Rate)" },
  { v: "engineers", l: "Engineers required" },
  { v: "description", l: "Description / brief of works" },
];

export function SurveyTemplatesTab() {
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(null);     // template being edited (or "new")
  const [busy, setBusy] = useState(false);

  const refresh = () => {
    Promise.all([
      api.get("/survey-templates"),
      api.get("/quote-categories"),
    ]).then(([t, c]) => {
      setTemplates(t.data || []);
      setCategories(c.data || []);
    });
  };
  useEffect(() => { refresh(); }, []);

  const startNew = () => {
    setEditing({
      name: "",
      product_type: categories[0]?.name || "",
      questions: [],
    });
  };

  const saveTemplate = async () => {
    setBusy(true);
    try {
      const body = {
        name: editing.name.trim(),
        product_type: editing.product_type,
        questions: editing.questions.map((q, i) => ({
          ...q,
          order: i,
          // Strip any blank lines users left while editing the options textarea
          options: (q.options || []).map((s) => (s || "").trim()).filter(Boolean),
        })),
      };
      if (!body.name) { toast.error("Name required"); setBusy(false); return; }
      if (!body.product_type) { toast.error("Product type required"); setBusy(false); return; }
      if (editing.id) {
        await api.put(`/survey-templates/${editing.id}`, body);
        toast.success("Template updated");
      } else {
        await api.post("/survey-templates", body);
        toast.success("Template created");
      }
      setEditing(null);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
    setBusy(false);
  };

  const deleteTemplate = async (id) => {
    if (!window.confirm("Delete this template? Existing surveys keep their snapshot.")) return;
    await api.delete(`/survey-templates/${id}`);
    refresh();
  };

  const togglePublished = async (t) => {
    const next = !(t.published !== false);   // missing field defaults to live
    try {
      await api.post(`/survey-templates/${t.id}/publish?published=${next}`);
      refresh();
      toast.success(next ? "Template set to LIVE" : "Template set to DRAFT");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update");
    }
  };

  return (
    <div className="space-y-4" data-testid="survey-templates-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-2xl font-bold">Survey Templates</h2>
          <p className="text-xs text-slate-500 mt-1">One template per product type. Surveyors pick a product type when starting a survey.</p>
        </div>
        <button onClick={startNew} className="h-9 px-3 bg-brand-blue text-white text-xs uppercase tracking-wider font-semibold inline-flex items-center gap-2"
          data-testid="new-survey-template">
          <Plus size={14} /> New template
        </button>
      </div>

      <div className="qb-card overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-slate-50 text-slate-600 uppercase text-[10px] tracking-wider">
            <tr>
              <th className="text-left px-4 py-3">Name</th>
              <th className="text-left px-4 py-3">Product type</th>
              <th className="text-left px-4 py-3">Questions</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-right px-4 py-3"></th>
            </tr>
          </thead>
          <tbody data-testid="templates-list">
            {templates.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400">No templates yet.</td></tr>
            )}
            {templates.map((t) => {
              const live = t.published !== false;
              return (
                <tr key={t.id} className="border-t border-slate-100">
                  <td className="px-4 py-2.5 font-semibold">{t.name}</td>
                  <td className="px-4 py-2.5">{t.product_type}</td>
                  <td className="px-4 py-2.5">{(t.questions || []).length}</td>
                  <td className="px-4 py-2.5">
                    <button
                      type="button"
                      onClick={() => togglePublished(t)}
                      data-testid={`template-published-toggle-${t.id}`}
                      title={live ? "Live — visible to surveyors. Click to set to draft." : "Draft — hidden from the surveyor picker. Click to publish."}
                      className={`inline-flex items-center gap-2 text-[10px] uppercase tracking-wider font-semibold transition`}
                    >
                      <span className={`relative inline-block w-9 h-5 rounded-full transition-colors ${live ? "bg-emerald-500" : "bg-slate-300"}`}>
                        <span className={`absolute top-0.5 ${live ? "left-[18px]" : "left-0.5"} w-4 h-4 rounded-full bg-white shadow transition-all`} />
                      </span>
                      {live ? <span className="text-emerald-700">Live</span> : <span className="text-slate-500">Draft</span>}
                    </button>
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <button onClick={() => setEditing(t)} className="text-xs uppercase tracking-wider font-semibold text-brand-blue hover:underline mr-3 inline-flex items-center gap-1">
                      <Edit3 size={11} /> Edit
                    </button>
                    <button onClick={() => deleteTemplate(t.id)} className="text-xs uppercase tracking-wider font-semibold text-red-600 hover:underline inline-flex items-center gap-1">
                      <Trash2 size={11} /> Delete
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      </div>

      {editing && (
        <TemplateEditor
          tpl={editing}
          categories={categories}
          onChange={setEditing}
          onCancel={() => setEditing(null)}
          onSave={saveTemplate}
          busy={busy}
        />
      )}
    </div>
  );
}

function TemplateEditor({ tpl, categories, onChange, onCancel, onSave, busy }) {
  const setField = (k, v) => onChange({ ...tpl, [k]: v });
  const setQuestion = (i, patch) => {
    const next = [...tpl.questions];
    next[i] = { ...next[i], ...patch };
    onChange({ ...tpl, questions: next });
  };
  const addQuestion = () => {
    const newId = (typeof crypto !== "undefined" && crypto.randomUUID)
      ? crypto.randomUUID()
      : `q-${Math.random().toString(36).slice(2)}-${Date.now().toString(36)}`;
    onChange({
      ...tpl,
      questions: [
        ...tpl.questions,
        { id: newId, label: "", type: "short_text", required: false, options: [], branching_rules: [] },
      ],
    });
  };
  const removeQuestion = (i) => onChange({ ...tpl, questions: tpl.questions.filter((_, idx) => idx !== i) });
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= tpl.questions.length) return;
    const next = [...tpl.questions];
    [next[i], next[j]] = [next[j], next[i]];
    onChange({ ...tpl, questions: next });
  };

  return (
    <div className="qb-card p-5 space-y-4 border-l-4 border-brand-orange" data-testid="template-editor">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <label className="block">
          <span className="block text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Template name</span>
          <input className="qb-input" value={tpl.name} onChange={(e) => setField("name", e.target.value)} data-testid="template-name" />
        </label>
        <label className="block">
          <span className="block text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Product type</span>
          <select className="qb-input" value={tpl.product_type} onChange={(e) => setField("product_type", e.target.value)} data-testid="template-product-type">
            <option value="">— pick —</option>
            {categories.map((c) => <option key={c.id || c.name}>{c.name}</option>)}
          </select>
        </label>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-semibold text-sm uppercase tracking-wider text-slate-700">Questions</h3>
          <button onClick={addQuestion} className="text-xs uppercase tracking-wider font-semibold text-brand-blue hover:underline inline-flex items-center gap-1" data-testid="add-question">
            <Plus size={12} /> Add question
          </button>
        </div>
        <div className="space-y-2">
          {tpl.questions.length === 0 && <div className="text-slate-400 text-sm">No questions yet.</div>}
          {tpl.questions.map((q, i) => (
            <div key={i} className="border border-slate-200 rounded-md p-3 grid grid-cols-12 gap-2 items-start bg-slate-50/50" data-testid={`question-row-${i}`}>
              <div className="col-span-1 flex flex-col items-center gap-1 pt-1.5 text-slate-400">
                <GripVertical size={12} />
                <button onClick={() => move(i, -1)} className="hover:text-slate-700" title="Move up"><ChevronUp size={12} /></button>
                <button onClick={() => move(i, 1)} className="hover:text-slate-700" title="Move down"><ChevronDown size={12} /></button>
              </div>
              <div className="col-span-5">
                <input className="qb-input" placeholder="Question label" value={q.label} onChange={(e) => setQuestion(i, { label: e.target.value })} data-testid={`question-label-${i}`} />
              </div>
              <div className="col-span-3">
                <select className="qb-input" value={q.type} onChange={(e) => setQuestion(i, { type: e.target.value })} data-testid={`question-type-${i}`}>
                  {TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
                </select>
              </div>
              <div className="col-span-2 inline-flex items-center gap-2 pt-2">
                <label className="text-xs inline-flex items-center gap-1.5">
                  <input type="checkbox" checked={q.required} onChange={(e) => setQuestion(i, { required: e.target.checked })} data-testid={`question-required-${i}`} />
                  Required
                </label>
              </div>
              <div className="col-span-1 text-right">
                <button onClick={() => removeQuestion(i)} className="text-red-500 hover:text-red-700" title="Remove"><Trash2 size={14} /></button>
              </div>
              {(q.type === "dropdown" || q.type === "multi_select") && (
                <div className="col-span-12 pl-7">
                  <span className="block text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Options (one per line)</span>
                  <textarea className="qb-input min-h-[64px]"
                    value={(q.options || []).join("\n")}
                    onChange={(e) => setQuestion(i, {
                      // Split on newline ONLY — do NOT trim or filter while the
                      // user is typing, otherwise trailing spaces (e.g. "Option ")
                      // and empty trailing newlines (the moment after pressing
                      // Enter) get stripped on every keystroke, the textarea
                      // value re-binds without them, and the cursor jumps back.
                      // Trimming + blank-line removal happens at save time
                      // (see saveTemplate body builder).
                      options: e.target.value.split("\n"),
                    })}
                    data-testid={`question-options-${i}`}
                    placeholder={`Option 1\nOption 2\nOption 3`}
                  />
                </div>
              )}
              <div className="col-span-12 pl-7">
                <input className="qb-input" placeholder="Help text (optional)"
                  value={q.help_text || ""}
                  onChange={(e) => setQuestion(i, { help_text: e.target.value })}
                />
              </div>
              <div className="col-span-12 pl-7 flex items-center gap-2 flex-wrap">
                <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Pre-fill quote field:</span>
                <select
                  className="qb-input max-w-[320px]"
                  value={q.maps_to || ""}
                  onChange={(e) => setQuestion(i, { maps_to: e.target.value || null })}
                  data-testid={`question-mapsto-${i}`}
                  title="When the surveyor converts this survey to a quote, the answer to this question will land in the chosen quote field automatically."
                >
                  {QUOTE_FIELD_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
                </select>
              </div>
              <BranchingEditor
                i={i}
                q={q}
                allQuestions={tpl.questions}
                onChange={(rules) => setQuestion(i, { branching_rules: rules })}
              />
              <LabourSeedEditor
                i={i}
                q={q}
                onChange={(seeds) => setQuestion(i, { labour_seeds: seeds })}
              />
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-200">
        <button onClick={onCancel} className="h-9 px-3 border border-slate-300 text-xs uppercase tracking-wider font-semibold">Cancel</button>
        <button onClick={onSave} disabled={busy} className="h-9 px-4 bg-brand-blue text-white text-xs uppercase tracking-wider font-semibold inline-flex items-center gap-2 disabled:opacity-50" data-testid="save-template">
          <Save size={12} /> {busy ? "Saving…" : tpl.id ? "Update template" : "Create template"}
        </button>
      </div>
    </div>
  );
}

function BranchingEditor({ i, q, allQuestions, onChange }) {
  const supported = branchingValuesFor(q);
  const rules = q.branching_rules || [];
  // Auto-open whenever rules exist OR the user has explicitly toggled it open.
  // Tracking via state keyed off the question id so the panel doesn't snap
  // shut when the rules array changes mid-edit.
  const [open, setOpen] = useState(rules.length > 0);
  useEffect(() => {
    if (rules.length > 0) setOpen(true);
    // intentionally only on initial rule presence
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q.id]);
  if (!supported) return null;

  // Include ALL later questions in the target dropdown — even ones with empty
  // labels, falling back to "Question N (no label yet)". This was previously
  // hiding new questions until they got a label, making branching look broken.
  const laterQuestions = allQuestions
    .slice(i + 1)
    .map((qq, offset) => ({ q: qq, indexLabel: i + 2 + offset }));

  const setRuleTarget = (value, targetId) => {
    const next = rules.filter((r) => r.value !== value);
    if (targetId) next.push({ value, target_id: targetId });
    onChange(next);
  };

  const ruleFor = (value) => rules.find((r) => r.value === value)?.target_id || "";
  const ruleCount = rules.filter((r) => r.target_id).length;

  return (
    <div className="col-span-12 pl-7">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-semibold text-brand-orange hover:underline mt-2"
        data-testid={`branching-toggle-${i}`}
      >
        <GitBranch size={11} />
        {ruleCount > 0 ? (
          <>
            {open ? "Hide" : "View"} conditional branching
            <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full bg-brand-orange text-white text-[10px] tracking-normal" data-testid={`branching-count-${i}`}>{ruleCount}</span>
          </>
        ) : (
          <>{open ? "Hide" : "Add"} conditional branching</>
        )}
      </button>
      {open && (
        <div className="mt-2 border border-orange-200 bg-orange-50/40 rounded p-3 space-y-2">
          <p className="text-[11px] text-slate-600">
            For each possible answer, optionally pick the next question to jump to. Questions in between are auto-hidden in the survey.
            Leave a row blank to follow the normal order.
          </p>
          {supported.map((val) => (
            <div key={val} className="grid grid-cols-12 gap-2 items-center">
              <div className="col-span-3 text-xs">
                <span className="text-slate-500">If answer =</span>{" "}
                <span className="font-semibold text-slate-900">{val}</span>
              </div>
              <div className="col-span-1 text-center text-slate-400">→</div>
              <div className="col-span-8">
                <select
                  className="qb-input"
                  value={ruleFor(val)}
                  onChange={(e) => setRuleTarget(val, e.target.value)}
                  data-testid={`branching-rule-${i}-${val.toLowerCase()}`}
                >
                  <option value="">— next question in order —</option>
                  {laterQuestions.map(({ q: lq, indexLabel }) => {
                    const labelText = (lq.label || "").trim() || `Question ${indexLabel} (no label yet)`;
                    return (
                      <option key={lq.id || indexLabel} value={lq.id || ""}>
                        Jump to: {labelText}
                      </option>
                    );
                  })}
                </select>
              </div>
            </div>
          ))}
          {laterQuestions.length === 0 && (
            <p className="text-[11px] text-amber-700">Add more questions below this one to make branching useful.</p>
          )}
        </div>
      )}
    </div>
  );
}

const WORK_TYPES = ["Install", "Repair", "Service", "Survey", "Travel", "Barrel Conversion"];

function LabourSeedEditor({ i, q, onChange }) {
  const supported = branchingValuesFor(q); // reuse: yes_no / dropdown / multi_select only
  const seeds = q.labour_seeds || [];
  const [open, setOpen] = useState(seeds.length > 0);
  useEffect(() => {
    if (seeds.length > 0) setOpen(true);
    // Re-open whenever seeds appear (e.g. after Edit-template re-hydrates
    // from the server) so admins can see their config immediately.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q.id, seeds.length]);
  if (!supported) return null;

  const seedFor = (val) => seeds.find((s) => (s.value || "").toLowerCase() === val.toLowerCase()) || {};
  const updateSeed = (val, patch) => {
    const others = seeds.filter((s) => (s.value || "").toLowerCase() !== val.toLowerCase());
    const next = { ...seedFor(val), value: val, ...patch };
    const hasContent = (next.hours && Number(next.hours) > 0) || (next.notes && next.notes.trim());
    onChange(hasContent ? [...others, next] : others);
  };
  const seedCount = seeds.filter((s) => Number(s.hours) > 0 || (s.notes || "").trim()).length;

  return (
    <div className="col-span-12 pl-7">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-semibold text-emerald-700 hover:underline mt-2"
        data-testid={`labour-seed-toggle-${i}`}
      >
        <Plus size={11} />
        {seedCount > 0
          ? <>{open ? "Hide" : "View"} auto-add labour rows
              <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full bg-emerald-600 text-white text-[10px] tracking-normal" data-testid={`labour-seed-count-${i}`}>{seedCount}</span>
            </>
          : <>{open ? "Hide" : "Add"} auto-add labour rows on convert</>}
      </button>
      {open && (
        <div className="mt-2 border border-emerald-200 bg-emerald-50/40 rounded p-3 space-y-2">
          <p className="text-[11px] text-slate-600">
            When this answer is given, automatically add a labour row to the converted quote.
          </p>
          {supported.map((val) => {
            const s = seedFor(val);
            return (
              <div key={val} className="grid grid-cols-12 gap-2 items-center">
                <div className="col-span-2 text-xs">
                  <span className="text-slate-500">If =</span>{" "}
                  <span className="font-semibold text-slate-900">{val}</span>
                </div>
                <div className="col-span-4">
                  <select
                    className="qb-input"
                    value={s.work_type || "Install"}
                    onChange={(e) => updateSeed(val, { work_type: e.target.value })}
                    data-testid={`labour-seed-worktype-${i}-${val.toLowerCase()}`}
                  >
                    {WORK_TYPES.map((w) => <option key={w}>{w}</option>)}
                  </select>
                </div>
                <div className="col-span-2">
                  <input
                    type="number"
                    step="0.25"
                    min="0"
                    className="qb-input"
                    placeholder="Hours"
                    value={s.hours ?? ""}
                    onChange={(e) => updateSeed(val, { hours: e.target.value === "" ? 0 : Number(e.target.value) })}
                    data-testid={`labour-seed-hours-${i}-${val.toLowerCase()}`}
                  />
                </div>
                <div className="col-span-4">
                  <input
                    className="qb-input"
                    placeholder="Notes (optional)"
                    value={s.notes || ""}
                    onChange={(e) => updateSeed(val, { notes: e.target.value })}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
